/**
 * Same-tab Meta embedded signup (phones). On a phone the JS SDK popup opens as a separate tab, so the login callback
 * and the WA_EMBEDDED_SIGNUP message do not come back reliably. Instead the whole tab goes to Meta's OAuth dialog
 * with the single-use state from /embedded-signup/start and the redirect URI the API derived for this environment.
 * Meta returns to /whatsapp/connect/callback; the portal worker moves state + code into the URL fragment, and the
 * callback screen POSTs them to /embedded-signup/redirect-complete (the API finds the WABA and number itself).
 */

import {
  metaErrorKind, OAUTH_ERROR_CODE, OAUTH_ERROR_TOKEN, ProviderErrorReportSchema, type MetaErrorInput, type ProviderErrorReport,
} from '@qm/contracts/meta-oauth-error';

/** Meta's OAuth dialog host. Navigating the top-level tab there needs no CSP entry. */
export const META_DIALOG_ORIGIN = 'https://www.facebook.com';
/** Same extras as the SDK popup (sessionInfoVersion 3). */
export const SIGNUP_EXTRAS = { setup: {}, featureType: '', sessionInfoVersion: '3' } as const;

export interface DialogInput { appId: string | null; configId: string | null; graphApiVersion: string; state: string; redirectUri?: string | null }

const VERSION = /^v\d{1,3}\.\d{1,3}$/;
const NUMERIC_ID = /^\d{1,32}$/;
const STATE = /^[0-9a-f]{16,128}$/;

/**
 * Meta OAuth dialog URL for the same-tab signup, or null when the start response is incomplete or malformed
 * (then no link is shown). The redirect URI must be an https portal URL ending in /whatsapp/connect/callback.
 */
export function buildSignupDialogUrl(s: DialogInput): string | null {
  if (!s.appId || !s.configId || !s.redirectUri) return null;
  if (!VERSION.test(s.graphApiVersion) || !NUMERIC_ID.test(s.appId) || !NUMERIC_ID.test(s.configId) || !STATE.test(s.state)) return null;
  let redirect: URL;
  try { redirect = new URL(s.redirectUri); } catch { return null; }
  if (redirect.protocol !== 'https:' || redirect.pathname !== '/whatsapp/connect/callback' || redirect.search || redirect.hash) return null;
  const q = new URLSearchParams({
    client_id: s.appId,
    config_id: s.configId,
    response_type: 'code',
    override_default_response_type: 'true',
    redirect_uri: redirect.toString(),
    state: s.state,
    extras: JSON.stringify(SIGNUP_EXTRAS),
  });
  return `${META_DIALOG_ORIGIN}/${s.graphApiVersion}/dialog/oauth?${q}`;
}

/** Phones and tablets: the SDK popup is unreliable there, so the main button uses the same-tab flow. */
export function isMobileBrowser(nav: { userAgent?: string; userAgentData?: { mobile?: boolean } } | undefined): boolean {
  if (!nav) return false;
  if (nav.userAgentData?.mobile === true) return true;
  return /Android|iPhone|iPad|iPod|Mobile|Silk|Opera Mini|IEMobile/i.test(nav.userAgent ?? '');
}

export type SignupCallback =
  | { kind: 'code'; state: string; code: string }
  /** the user cancelled or declined in Meta; report holds Meta's codes for the audit */
  | { kind: 'cancelled'; report: ProviderErrorReport }
  /** Meta ended the signup with another error; errorKey is the plain-language message */
  | { kind: 'provider_error'; errorKey: string; report: ProviderErrorReport }
  | { kind: 'invalid' };

/** Translation key for a Meta signup error that is not a cancel. */
export function providerErrorKey(e: MetaErrorInput): string {
  const kind = metaErrorKind(e);
  if (kind === 'permissions') return 'whatsapp-connect.signup.metaPermissions';
  if (kind === 'unavailable') return 'whatsapp-connect.signup.metaUnavailable';
  return 'whatsapp-connect.signup.failed';
}

const CODE = /^[\x21-\x7e]{8,2048}$/;

/**
 * Parse the fragment the portal worker wrote (`#state=&code=` or `#state=&error=[&error_reason=][&error_code=]`).
 * Anything malformed is invalid; a user cancel in Meta is "cancelled", any other Meta error is "provider_error".
 * Never trusts more than state + code, or state + Meta's validated short codes.
 */
export function parseSignupCallback(hash: string): SignupCallback {
  const p = new URLSearchParams(hash.replace(/^#/, ''));
  const state = p.get('state') ?? '';
  const code = p.get('code') ?? '';
  const error = p.get('error');
  if (error) {
    if (!STATE.test(state) || error === 'invalid_callback') return { kind: 'invalid' };
    const reason = p.get('error_reason') ?? '';
    const errorCode = p.get('error_code') ?? '';
    const parsed = ProviderErrorReportSchema.safeParse({
      state, error,
      ...(OAUTH_ERROR_TOKEN.test(reason) ? { errorReason: reason } : {}),
      ...(OAUTH_ERROR_CODE.test(errorCode) ? { errorCode } : {}),
    });
    if (!parsed.success) return { kind: 'invalid' };
    const report = parsed.data;
    return metaErrorKind(report) === 'denied' ? { kind: 'cancelled', report } : { kind: 'provider_error', errorKey: providerErrorKey(report), report };
  }
  if (!STATE.test(state) || !CODE.test(code)) return { kind: 'invalid' };
  return { kind: 'code', state, code };
}
